import { useState } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import type {
  BlipSwitchAction,
  BlipSwitchLogEntry,
  BlipSwitchState,
  BlipSwitchStopResult,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { WHATSAPP_KEY, errorMessage } from './whatsapp-queries';

/**
 * O interruptor da Blip (spec 025, fase 3): "Devolver todos os contatos ao
 * fluxo da Blip". Parar o disparo pela Blip e desmarcar todo contato que o ERP
 * marcou no roteador — a resposta deles volta para o fluxo antigo da Blip.
 * Religar o disparo é a outra ação. Só admin (a API responde 403 aos demais).
 */
const blipSwitchKey = [...WHATSAPP_KEY, 'blip-switch'] as const;

export function blipSwitchOptions() {
  return queryOptions({
    queryKey: blipSwitchKey,
    queryFn: async ({ signal }) => {
      const res = await api<BlipSwitchState>('/whatsapp/blip-switch', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

const ACTION_LABELS: Record<BlipSwitchAction, string> = {
  stop: 'Parou o disparo pela Blip',
  unmark_all: 'Devolveu os contatos',
  resume: 'Religou o disparo pela Blip',
};

function logDetail(l: BlipSwitchLogEntry): string | null {
  if (l.action !== 'unmark_all') return null;
  const c = l.counts;
  const parts = [
    c
      ? `${c.unmarked} devolvidos, ${c.skipped} já estavam no fluxo da Blip, ${c.failed} falharam`
      : null,
    l.error ? `erro: ${l.error}` : null,
  ];
  return parts.filter(Boolean).join(' · ') || null;
}

export function BlipSwitchSection() {
  const qc = useQueryClient();
  const q = useQuery(blipSwitchOptions());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastRun, setLastRun] = useState<BlipSwitchStopResult | null>(null);

  const stop = useMutation({
    mutationFn: async () => {
      const res = await api<BlipSwitchStopResult>('/whatsapp/blip-switch/stop', { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      setLastRun(data);
      setConfirmOpen(false);
      qc.setQueryData(blipSwitchKey, data.state);
    },
  });
  const resume = useMutation({
    mutationFn: async () => {
      const res = await api<BlipSwitchState>('/whatsapp/blip-switch/resume', { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      setLastRun(null);
      qc.setQueryData(blipSwitchKey, data);
    },
  });

  if (q.isPending) return <Skeleton className="h-24 w-full" aria-busy />;
  if (q.isError)
    return <EmptyState message={`Erro ao carregar o interruptor: ${errorMessage(q.error)}`} />;
  const s = q.data;

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldAlert className="size-4 text-muted-foreground" />
        <span className="font-medium">Disparo pela Blip</span>
        {s.dispatchStopped ? (
          <Badge variant="destructive">Parado</Badge>
        ) : (
          <Badge variant="success">Liberado</Badge>
        )}
        {s.dispatchStopped && s.stoppedAt ? (
          <span className="text-xs text-muted-foreground">
            desde {formatDateTime(s.stoppedAt)}
            {s.stoppedBy ? ` · por ${s.stoppedBy}` : ''}
          </span>
        ) : null}
      </div>

      <p className="text-xs text-muted-foreground">
        Quando o ERP manda o modelo pelo número da Blip, o contato é marcado no roteador para que a
        resposta dele caia no nosso bot. Ao finalizar a conversa (ou fechar o ticket), o ERP
        desmarca sozinho. Este botão faz tudo de uma vez: para os disparos novos pela Blip e devolve
        TODOS os contatos marcados ao fluxo da Blip.
      </p>
      <p className="text-xs text-muted-foreground">
        Números: {s.channels.map((c) => c.label + (c.active ? '' : ' (inativo)')).join(', ')}
        {s.pendingUnmarks > 0 ? ` · ${s.pendingUnmarks} desmarcação(ões) na fila` : ''}
      </p>
      {s.failedUnmarks > 0 ? (
        <p className="text-xs text-destructive">
          {s.failedUnmarks} desmarcação(ões) desistiram nos últimos 7 dias — algum contato pode ter
          ficado marcado. Devolver todos os contatos resolve.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={stop.isPending}
        >
          Devolver todos os contatos ao fluxo da Blip
        </Button>
        {s.dispatchStopped ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => resume.mutate()}
            disabled={resume.isPending}
          >
            {resume.isPending ? 'Religando…' : 'Religar o disparo pela Blip'}
          </Button>
        ) : null}
      </div>
      {resume.error ? (
        <p className="text-xs text-destructive">{errorMessage(resume.error)}</p>
      ) : null}

      {lastRun ? (
        <div
          className={`rounded-md border p-2 text-xs ${lastRun.complete ? 'bg-muted/30' : 'border-destructive/40 bg-destructive/5'}`}
        >
          <div className="mb-1 font-medium">
            {lastRun.complete
              ? 'Disparo parado e contatos devolvidos.'
              : 'Disparo parado, mas o broker não confirmou a devolução de todos — tente de novo.'}
          </div>
          <ul className="space-y-0.5">
            {lastRun.runs.map((r) => (
              <li key={r.channelId}>
                {r.label}:{' '}
                {r.outcome === 'skipped'
                  ? 'nada a fazer (integração desligada)'
                  : r.counts
                    ? `${r.counts.total} marcados · ${r.counts.unmarked} devolvidos · ${r.counts.skipped} já no fluxo da Blip · ${r.counts.failed} falharam`
                    : 'sem resposta do broker'}
                {r.outcome === 'failed' && r.error ? ` (${r.error}, ${r.attempts} tentativas)` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {s.log.length > 0 ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Registro</summary>
          <ul className="mt-1 space-y-0.5">
            {s.log.map((l) => (
              <li key={l.id}>
                {formatDateTime(l.at)} · {ACTION_LABELS[l.action]}
                {l.userName ? ` · ${l.userName}` : ''}
                {logDetail(l) ? ` · ${logDetail(l)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Devolver todos os contatos ao fluxo da Blip?"
      >
        <p className="text-sm text-muted-foreground">
          O ERP para de disparar pela Blip e pede ao broker que desmarque todos os contatos. A
          partir daí, a resposta de qualquer contato cai no fluxo da Blip, e não no nosso bot —
          inclusive quem estiver no meio de uma conversa com o bot. Religar o disparo é outra ação,
          depois.
        </p>
        {stop.error ? (
          <p className="mt-2 text-sm text-destructive">{errorMessage(stop.error)}</p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={() => stop.mutate()} disabled={stop.isPending}>
            {stop.isPending ? 'Devolvendo…' : 'Devolver todos'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
